import { FC, useMemo, useState, useCallback, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { FormField } from "../../../../types/Form";
import FormCard from "../../../../components/Form/El/FormCard";
import { isValidEmail } from "../../../../helpers/Validation";
import { setNotification } from "../../../../helpers/cache";
import { LoadingDesign } from "../../../../helpers";
import { GET_CLIENT_META_INFO_QUERY } from "../../Services/Queries/ClientMetaInfoQuery";
import { CREATE_OR_UPDATE_CLIENT_META_INFO } from "../../Services/Mutations/Mutation";

interface EditClientMetaInfoProps {
  clientId?: number;
  clientEditData?: any;
  setNewClientData?: (value: any) => void;
  tabsAccess?: any;
  setIsNeedUpdateClient?: (value: boolean) => void;
  setMetaInfoData?: (value: any) => void;
}

interface ClientMetaInfoData {
  companyName?: string;
  website?: string;
  existingVendor?: string;
  mutualConnection?: string;
  upcomingEvent?: string;
  newCampaignRebrand?: string;
  hiringExpansion?: string;
  csrSustainability?: string;
  recentNews?: string;
  keyTimingInsight?: string;
  whatMattersMost?: string;
  challengesFacing?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  fax?: string;
  howWeHelp?: string;
  proofPointExample?: string;
  subjectLineIdea?: string;
  personalizedOpener?: string;
  visualAttachment?: string;
  cta?: string;
  followUpSequence?: string;
  nextStepStatus?: string;
}

const EditClientMetaInfo: FC<EditClientMetaInfoProps> = ({
  clientId,
  clientEditData,
  setNewClientData,
  tabsAccess,
  setIsNeedUpdateClient,
  setMetaInfoData: setParentMetaInfoData,
}) => {
  const [metaInfoData, setMetaInfoData] = useState<ClientMetaInfoData>({
    companyName: "",
    website: "",
    existingVendor: "",
    mutualConnection: "",
    upcomingEvent: "",
    newCampaignRebrand: "",
    hiringExpansion: "",
    csrSustainability: "",
    recentNews: "",
    keyTimingInsight: "",
    whatMattersMost: "",
    challengesFacing: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    fax: "",
    howWeHelp: "",
    proofPointExample: "",
    subjectLineIdea: "",
    personalizedOpener: "",
    visualAttachment: "",
    cta: "",
    followUpSequence: "",
    nextStepStatus: "",
  });

  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingFetch, setIsLoadingFetch] = useState(false);

  // GraphQL Mutation
  const [createOrUpdateClientMetaInfo] = useMutation(
    CREATE_OR_UPDATE_CLIENT_META_INFO,
    {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: error?.message }]);
        setIsLoadingSave(false);
      },
      onCompleted: (data) => {
        if (data?.createOrUpdateClientMetaInfo) {
          setNotification([
            {
              type: "SUCCESS",
              message: "Meta info saved successfully.",
            },
          ]);
          setIsNeedUpdateClient?.(true);
        }
        setIsLoadingSave(false);
      },
    }
  );

  // GraphQL Query
  const [fetchClientMetaInfo] = useLazyQuery(GET_CLIENT_META_INFO_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
      setIsLoadingFetch(false);
    },
    onCompleted: (data) => {
      if (data?.clientMetaInfo) {
        const metaInfo = data.clientMetaInfo;
        setMetaInfoData({
          companyName: metaInfo.companyName || "",
          website: metaInfo.website || "",
          existingVendor: metaInfo.existingVendor || "",
          mutualConnection: metaInfo.mutualConnection || "",
          upcomingEvent: metaInfo.upcomingEvent || "",
          newCampaignRebrand: metaInfo.newCampaignRebrand || "",
          hiringExpansion: metaInfo.hiringExpansion || "",
          csrSustainability: metaInfo.csrSustainability || "",
          recentNews: metaInfo.recentNews || "",
          keyTimingInsight: metaInfo.keyTimingInsight || "",
          whatMattersMost: metaInfo.whatMattersMost || "",
          challengesFacing: metaInfo.challengesFacing || "",
          primaryContactName: metaInfo.primaryContactName || "",
          primaryContactEmail: metaInfo.primaryContactEmail || "",
          primaryContactPhone: metaInfo.primaryContactPhone || "",
          fax: metaInfo.fax || "",
          howWeHelp: metaInfo.howWeHelp || "",
          proofPointExample: metaInfo.proofPointExample || "",
          subjectLineIdea: metaInfo.subjectLineIdea || "",
          personalizedOpener: metaInfo.personalizedOpener || "",
          visualAttachment: metaInfo.visualAttachment || "",
          cta: metaInfo.cta || "",
          followUpSequence: metaInfo.followUpSequence || "",
          nextStepStatus: metaInfo.nextStepStatus || "",
        });
      }
      setIsLoadingFetch(false);
    },
  });

  // Fetch meta info when component mounts or clientId changes
  useEffect(() => {
    if (clientId) {
      setIsLoadingFetch(true);
      fetchClientMetaInfo({
        variables: { clientId },
      });
    }
  }, [clientId, fetchClientMetaInfo]);

  // Sync local meta info state with parent state
  useEffect(() => {
    if (setParentMetaInfoData) {
      setParentMetaInfoData(metaInfoData);
    }
  }, [metaInfoData, setParentMetaInfoData]);

  // ACL Access checks
  const metaInfoFormAccess = useMemo(
    () =>
      tabsAccess?.["meta-info"]?.forms?.clientMetaInfo ||
      tabsAccess?.metaInfo?.forms?.clientMetaInfo,
    [tabsAccess]
  );

  const metaInfoFormFields = useMemo(
    () => metaInfoFormAccess?.fields,
    [metaInfoFormAccess]
  );

  const saveButtonAccess = useMemo(
    () => metaInfoFormAccess?.buttons?.includes("save"),
    [metaInfoFormAccess]
  );

  // Debug logging
  useEffect(() => {
    console.log("MetaInfo Debug:", {
      tabsAccess,
      metaInfoFormAccess,
      metaInfoFormFields,
      saveButtonAccess,
    });
  }, [tabsAccess, metaInfoFormAccess, metaInfoFormFields, saveButtonAccess]);

  // Update meta info data
  const updateMetaInfoData = useCallback(
    (field: keyof ClientMetaInfoData, value: string) => {
      setMetaInfoData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  // Company Research Fields
  const companyResearchFields = useMemo(
    (): FormField[] => [
      {
        name: "companyName",
        label: "Company Name",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("companyName", value);
          }
        },
        type: "input",
        value: metaInfoData.companyName,
        accessedFields: metaInfoFormFields?.companyName,
      },
      {
        name: "website",
        label: "Website",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("website", value);
          }
        },
        type: "input",
        value: metaInfoData.website,
        accessedFields: metaInfoFormFields?.website,
      },
      {
        name: "existingVendor",
        label: "Existing Vendor",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("existingVendor", value);
          }
        },
        type: "input",
        value: metaInfoData.existingVendor,
        accessedFields: metaInfoFormFields?.existingVendor,
      },
      {
        name: "mutualConnection",
        label: "Mutual Connection",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("mutualConnection", value);
          }
        },
        type: "input",
        value: metaInfoData.mutualConnection,
        accessedFields: metaInfoFormFields?.mutualConnection,
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  // Timing & Opportunities Fields
  const timingOpportunitiesFields = useMemo(
    (): FormField[] => [
      {
        name: "upcomingEvent",
        label: "Upcoming Event",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("upcomingEvent", value);
          }
        },
        type: "input",
        value: metaInfoData.upcomingEvent,
        accessedFields: metaInfoFormFields?.upcomingEvent,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "newCampaignRebrand",
        label: "New Campaign/Rebrand",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("newCampaignRebrand", value);
          }
        },
        type: "input",
        value: metaInfoData.newCampaignRebrand,
        accessedFields: metaInfoFormFields?.newCampaignRebrand,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "hiringExpansion",
        label: "Hiring/Expansion",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("hiringExpansion", value);
          }
        },
        type: "input",
        value: metaInfoData.hiringExpansion,
        accessedFields: metaInfoFormFields?.hiringExpansion,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "csrSustainability",
        label: "CSR/Sustainability",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("csrSustainability", value);
          }
        },
        type: "input",
        value: metaInfoData.csrSustainability,
        accessedFields: metaInfoFormFields?.csrSustainability,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "recentNews",
        label: "Recent News",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("recentNews", value);
          }
        },
        type: "input",
        value: metaInfoData.recentNews,
        accessedFields: metaInfoFormFields?.recentNews,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "keyTimingInsight",
        label: "Key Timing Insight",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("keyTimingInsight", value);
          }
        },
        type: "input",
        value: metaInfoData.keyTimingInsight,
        accessedFields: metaInfoFormFields?.keyTimingInsight,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  // Client Needs & Challenges Fields
  const clientNeedsFields = useMemo(
    (): FormField[] => [
      {
        name: "whatMattersMost",
        label: "What Matters Most",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("whatMattersMost", value);
          }
        },
        type: "input",
        value: metaInfoData.whatMattersMost,
        accessedFields: metaInfoFormFields?.whatMattersMost,
        inputOptions: { isTextarea: true, rowCount: 4 },
      },
      {
        name: "challengesFacing",
        label: "Challenges Facing",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("challengesFacing", value);
          }
        },
        type: "input",
        value: metaInfoData.challengesFacing,
        accessedFields: metaInfoFormFields?.challengesFacing,
        inputOptions: { isTextarea: true, rowCount: 4 },
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  // Primary Contact Fields
  const primaryContactFields = useMemo(
    (): FormField[] => [
      {
        name: "primaryContactName",
        label: "Primary Contact Name",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("primaryContactName", value);
          }
        },
        type: "input",
        value: metaInfoData.primaryContactName,
        accessedFields: metaInfoFormFields?.primaryContactName,
      },
      {
        name: "primaryContactEmail",
        label: "Primary Contact Email",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("primaryContactEmail", value);
          }
        },
        type: "input",
        value: metaInfoData.primaryContactEmail,
        accessedFields: metaInfoFormFields?.primaryContactEmail,
        isIncorrectValue: metaInfoData.primaryContactEmail
          ? !isValidEmail(metaInfoData.primaryContactEmail)
          : false,
      },
      {
        name: "primaryContactPhone",
        label: "Primary Contact Phone",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("primaryContactPhone", value);
          }
        },
        type: "input",
        value: metaInfoData.primaryContactPhone,
        accessedFields: metaInfoFormFields?.primaryContactPhone,
      },
      {
        name: "fax",
        label: "Fax",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("fax", value);
          }
        },
        type: "input",
        value: metaInfoData.fax,
        accessedFields: metaInfoFormFields?.fax,
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  // Outreach Strategy Fields
  const outreachStrategyFields = useMemo(
    (): FormField[] => [
      {
        name: "howWeHelp",
        label: "How We Help",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("howWeHelp", value);
          }
        },
        type: "input",
        value: metaInfoData.howWeHelp,
        accessedFields: metaInfoFormFields?.howWeHelp,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "proofPointExample",
        label: "Proof Point/Example",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("proofPointExample", value);
          }
        },
        type: "input",
        value: metaInfoData.proofPointExample,
        accessedFields: metaInfoFormFields?.proofPointExample,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "subjectLineIdea",
        label: "Subject Line Idea",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("subjectLineIdea", value);
          }
        },
        type: "input",
        value: metaInfoData.subjectLineIdea,
        accessedFields: metaInfoFormFields?.subjectLineIdea,
        // inputOptions: { isTextarea: true, rowCount: 2 },
      },
      {
        name: "personalizedOpener",
        label: "Personalized Opener",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("personalizedOpener", value);
          }
        },
        type: "input",
        value: metaInfoData.personalizedOpener,
        accessedFields: metaInfoFormFields?.personalizedOpener,
        // inputOptions: { isTextarea: true, rowCount: 3 },
      },
      {
        name: "visualAttachment",
        label: "Visual/Attachment",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("visualAttachment", value);
          }
        },
        type: "input",
        value: metaInfoData.visualAttachment,
        accessedFields: metaInfoFormFields?.visualAttachment,
      },
      {
        name: "cta",
        label: "Call to Action (CTA)",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("cta", value);
          }
        },
        type: "input",
        value: metaInfoData.cta,
        accessedFields: metaInfoFormFields?.cta,
        // inputOptions: { isTextarea: true, rowCount: 2 },
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  // Follow-up & Status Fields
  const followUpFields = useMemo(
    (): FormField[] => [
      {
        name: "followUpSequence",
        label: "Follow-up Sequence",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("followUpSequence", value);
          }
        },
        type: "input",
        value: metaInfoData.followUpSequence,
        accessedFields: metaInfoFormFields?.followUpSequence,
        inputOptions: { isTextarea: true, rowCount: 4 },
      },
      {
        name: "nextStepStatus",
        label: "Next Step/Status",
        onChange: (value) => {
          if (typeof value === "string") {
            updateMetaInfoData("nextStepStatus", value);
          }
        },
        type: "input",
        value: metaInfoData.nextStepStatus,
        accessedFields: metaInfoFormFields?.nextStepStatus,
        inputOptions: { isTextarea: true, rowCount: 3 },
      },
    ],
    [metaInfoData, metaInfoFormFields, updateMetaInfoData]
  );

  const onSaveMetaInfo = useCallback(async () => {
    if (!clientId) return;

    setIsLoadingSave(true);

    await createOrUpdateClientMetaInfo({
      variables: {
        clientId,
        companyName: metaInfoData.companyName,
        website: metaInfoData.website,
        existingVendor: metaInfoData.existingVendor,
        mutualConnection: metaInfoData.mutualConnection,
        upcomingEvent: metaInfoData.upcomingEvent,
        newCampaignRebrand: metaInfoData.newCampaignRebrand,
        hiringExpansion: metaInfoData.hiringExpansion,
        csrSustainability: metaInfoData.csrSustainability,
        recentNews: metaInfoData.recentNews,
        keyTimingInsight: metaInfoData.keyTimingInsight,
        whatMattersMost: metaInfoData.whatMattersMost,
        challengesFacing: metaInfoData.challengesFacing,
        primaryContactName: metaInfoData.primaryContactName,
        primaryContactEmail: metaInfoData.primaryContactEmail,
        primaryContactPhone: metaInfoData.primaryContactPhone,
        fax: metaInfoData.fax,
        howWeHelp: metaInfoData.howWeHelp,
        proofPointExample: metaInfoData.proofPointExample,
        subjectLineIdea: metaInfoData.subjectLineIdea,
        personalizedOpener: metaInfoData.personalizedOpener,
        visualAttachment: metaInfoData.visualAttachment,
        cta: metaInfoData.cta,
        followUpSequence: metaInfoData.followUpSequence,
        nextStepStatus: metaInfoData.nextStepStatus,
      },
    });
  }, [clientId, metaInfoData, createOrUpdateClientMetaInfo]);

  if (isLoadingFetch) {
    return <LoadingDesign />;
  }

  return (
    <section>
      <div className="row-fluid">
        {/* Company Research */}
        {metaInfoFormAccess && (
          <FormCard
            name="company-research"
            formFields={[companyResearchFields]}
            className="box-6"
            label="Company Research"
          />
        )}

        {/* Primary Contact */}
        {metaInfoFormAccess && (
          <FormCard
            name="primary-contact"
            formFields={[primaryContactFields]}
            className="box-6"
            label="Primary Contact"
          />
        )}
      </div>

      <div className="row-fluid">
        {/* Timing & Opportunities */}
        {metaInfoFormAccess && (
          <FormCard
            name="timing-opportunities"
            formFields={[timingOpportunitiesFields]}
            className="box-6"
            label="Timing & Opportunities"
          />
        )}

        {/* Client Needs & Challenges */}
        {metaInfoFormAccess && (
          <FormCard
            name="client-needs"
            formFields={[clientNeedsFields]}
            className="box-6"
            label="Client Needs & Challenges"
          />
        )}
      </div>

      <div className="row-fluid">
        {/* Outreach Strategy */}
        {metaInfoFormAccess && (
          <FormCard
            name="outreach-strategy"
            formFields={[outreachStrategyFields]}
            className="box-6"
            label="Outreach Strategy"
          />
        )}

        {/* Follow-up & Status */}
        {metaInfoFormAccess && (
          <FormCard
            name="follow-up"
            formFields={[followUpFields]}
            className="box-6"
            label="Follow-up & Status"
          />
        )}
      </div>

      {/* Standalone Save Button */}
      {saveButtonAccess && (
        <div className="row-fluid mt-4 flex justify-end">
          <div className="span12">
            <button
              onClick={onSaveMetaInfo}
              className="btn btn-small btn-success"
              disabled={isLoadingSave}
            >
              <i
                className={
                  isLoadingSave
                    ? "inline-block animate-spin icon-spin4"
                    : "icon-floppy"
                }
              />
              &nbsp; Save
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default EditClientMetaInfo;
